import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { ChevronDown, Check, AlertTriangle, RefreshCw, Eye } from "lucide-react";
import { formatDate } from "@/lib/dates";

type InspectionItem = {
  id: string;
  inspection_id: string;
  category: string;
  item_key: string;
  label: string;
  status: string;
  note: string;
  resolved_at: string | null;
  resolved_by: string | null;
};

type Inspection = {
  id: string;
  vehicle_number: string;
  driver_name: string;
  created_at: string;
  overall_status: string;
  notes: string;
  inspection_type: string | null;
  items?: InspectionItem[];
};

type Props = {
  vehicleNumber: string;
  inspectionType: "driver" | "admin";
  latestInspection: Inspection | null;
  onRefresh?: () => void;
};

function daysBetween(dateIsoOrTs: string) {
  const d = new Date(dateIsoOrTs);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

const STATUS_COLORS: Record<string, string> = {
  OK: "text-green-400",
  "Needs Attention": "text-yellow-400",
  "Needs Fixing Soon": "text-orange-400",
  Unsafe: "text-red-400",
};

const VehicleInspectionDropdown: React.FC<Props> = ({
  vehicleNumber,
  inspectionType,
  latestInspection,
  onRefresh,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loadedCount, setLoadedCount] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedInspectionId, setExpandedInspectionId] = useState<string | null>(null);
  const [markingOk, setMarkingOk] = useState<string | null>(null);

  const loadInspections = async (count: number) => {
    setLoading(true);
    try {
      let query = supabase
        .from("vehicle_inspections")
        .select("id, vehicle_number, driver_name, created_at, overall_status, notes, inspection_type")
        .eq("vehicle_number", vehicleNumber)
        // Finished inspections only: one that was started and not completed isn't an inspection yet.
        .not("completed_at", "is", null)
        .order("created_at", { ascending: false })
        .limit(count + 1);

      if (inspectionType === "driver") {
        query = query.or("inspection_type.is.null,inspection_type.eq.driver");
      } else {
        query = query.eq("inspection_type", "admin");
      }

      const { data, error } = await query;

      if (error) throw error;

      const results = data || [];
      setHasMore(results.length > count);
      setInspections(results.slice(0, count));
    } catch (e: any) {
      toast({ title: "Error loading inspections", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadInspectionItems = async (inspectionId: string) => {
    try {
      const { data, error } = await supabase
        .from("vehicle_inspection_items")
        .select("id, inspection_id, category, item_key, label, status, note, resolved_at, resolved_by")
        .eq("inspection_id", inspectionId)
        .order("category");

      if (error) throw error;

      setInspections((prev) =>
        prev.map((insp) =>
          insp.id === inspectionId ? { ...insp, items: data || [] } : insp
        )
      );
    } catch (e: any) {
      toast({ title: "Error loading items", description: e.message, variant: "destructive" });
    }
  };

  useEffect(() => {
    if (open && vehicleNumber) {
      loadInspections(loadedCount);
    }
  }, [open, vehicleNumber, loadedCount]);

  const handleLoadMore = () => {
    setLoadedCount((prev) => prev + 4);
  };

  const handleToggleExpand = async (inspectionId: string) => {
    if (expandedInspectionId === inspectionId) {
      setExpandedInspectionId(null);
    } else {
      setExpandedInspectionId(inspectionId);
      const inspection = inspections.find((i) => i.id === inspectionId);
      if (!inspection?.items) {
        await loadInspectionItems(inspectionId);
      }
    }
  };

  const handleMarkItemOk = async (item: InspectionItem) => {
    setMarkingOk(item.id);
    try {
      const driverName = localStorage.getItem("driver_name") || "Admin";
      const now = new Date().toISOString();

      const { error } = await supabase
        .from("vehicle_inspection_items")
        .update({
          status: "OK",
          resolved_at: now,
          resolved_by: driverName,
        })
        .eq("id", item.id);

      if (error) throw error;

      // Update local state
      setInspections((prev) =>
        prev.map((insp) => ({
          ...insp,
          items: insp.items?.map((i) =>
            i.id === item.id
              ? { ...i, status: "OK", resolved_at: now, resolved_by: driverName }
              : i
          ),
        }))
      );

      // Check if all items are now OK and update overall status
      const inspection = inspections.find((i) => i.id === item.inspection_id);
      if (inspection?.items) {
        const allOk = inspection.items.every((i) =>
          i.id === item.id ? true : i.status === "OK"
        );

        if (allOk) {
          await supabase
            .from("vehicle_inspections")
            .update({ overall_status: "OK" })
            .eq("id", item.inspection_id);

          setInspections((prev) =>
            prev.map((insp) =>
              insp.id === item.inspection_id ? { ...insp, overall_status: "OK" } : insp
            )
          );
        }
      }

      toast({ title: "Item marked as OK" });
      onRefresh?.();
    } catch (e: any) {
      toast({ title: "Error updating item", description: e.message, variant: "destructive" });
    } finally {
      setMarkingOk(null);
    }
  };

  // Determine display for the trigger button
  const daysAgo = latestInspection ? daysBetween(latestInspection.created_at) : null;
  const threshold = inspectionType === "admin" ? 30 : 7;
  const isOverdue = daysAgo !== null && daysAgo > threshold;
  const hasIssue = latestInspection?.overall_status && latestInspection.overall_status !== "OK";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-auto py-1 px-2 justify-start font-normal ${
            hasIssue ? "text-red-400" : isOverdue ? "text-red-400" : "text-muted-foreground"
          }`}
        >
          {latestInspection ? (
            <div className="flex flex-col items-start text-left">
              <div className="flex items-center gap-1">
                <span className="font-medium">{formatDate(latestInspection.created_at)}</span>
                {hasIssue && <AlertTriangle className="w-3 h-3 text-red-400" />}
                <ChevronDown className="w-3 h-3 ml-1" />
              </div>
              <span className="text-xs">{latestInspection.driver_name || "Unknown"}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span>No inspection</span>
              <RefreshCw className="w-3 h-3" />
            </div>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-96 p-0" align="start">
        <div className="p-3 border-b">
          <div className="font-semibold text-sm">
            {inspectionType === "admin" ? "Admin" : "Driver"} Inspections - {vehicleNumber}
          </div>
          <div className="text-xs text-muted-foreground">
            Click an inspection to view details and resolve issues
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading && inspections.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">Loading...</div>
          ) : inspections.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">No inspections found</div>
          ) : (
            <div className="divide-y">
              {inspections.map((inspection, index) => {
                const isExpanded = expandedInspectionId === inspection.id;
                const hasIssues = inspection.overall_status !== "OK";
                const failedItems = inspection.items?.filter((i) => i.status !== "OK") || [];
                const resolvedItems = inspection.items?.filter(
                  (i) => i.status === "OK" && i.resolved_at
                ) || [];

                return (
                  <div key={inspection.id} className="bg-card">
                    {/* Inspection header */}
                    <button
                      onClick={() => handleToggleExpand(inspection.id)}
                      className="w-full p-3 text-left hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {formatDate(inspection.created_at)}
                          </span>
                          {index === 0 && (
                            <Badge variant="outline" className="text-xs">Latest</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={hasIssues ? "destructive" : "outline"}
                            className={`text-xs ${!hasIssues ? "text-green-400 border-green-400/50" : ""}`}
                          >
                            {inspection.overall_status}
                          </Badge>
                          <Eye className="w-3 h-3 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        By: {inspection.driver_name || "Unknown"}
                      </div>
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-2">
                        {!inspection.items ? (
                          <div className="text-sm text-muted-foreground">Loading items...</div>
                        ) : (
                          <>
                            {/* Failed items that need attention */}
                            {failedItems.length > 0 && (
                              <div className="space-y-2">
                                <div className="text-xs font-semibold text-red-400">
                                  Issues Requiring Attention:
                                </div>
                                {failedItems.map((item) => (
                                  <div
                                    key={item.id}
                                    className="bg-red-500/10 border border-red-500/30 rounded-lg p-2"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="text-sm font-medium">{item.label}</div>
                                        <div className="text-xs text-muted-foreground">
                                          {item.category}
                                        </div>
                                        <Badge
                                          variant="outline"
                                          className={`text-xs mt-1 ${STATUS_COLORS[item.status] || ""}`}
                                        >
                                          {item.status}
                                        </Badge>
                                        {item.note && (
                                          <div className="text-xs text-muted-foreground mt-1">
                                            Note: {item.note}
                                          </div>
                                        )}
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-green-400 border-green-400/50 hover:bg-green-400/10"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMarkItemOk(item);
                                        }}
                                        disabled={markingOk === item.id}
                                      >
                                        {markingOk === item.id ? (
                                          <RefreshCw className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <>
                                            <Check className="w-3 h-3 mr-1" />
                                            Mark OK
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Previously resolved items */}
                            {resolvedItems.length > 0 && (
                              <div className="space-y-1">
                                <div className="text-xs font-semibold text-green-400">
                                  Previously Resolved:
                                </div>
                                {resolvedItems.map((item) => (
                                  <div
                                    key={item.id}
                                    className="bg-green-500/10 border border-green-500/30 rounded-lg p-2"
                                  >
                                    <div className="text-sm">{item.label}</div>
                                    <div className="text-xs text-green-400">
                                      Previous issue - marked OK {formatDate(item.resolved_at)}
                                      {item.resolved_by && ` by ${item.resolved_by}`}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* All OK message */}
                            {failedItems.length === 0 && resolvedItems.length === 0 && (
                              <div className="text-sm text-green-400 flex items-center gap-2">
                                <Check className="w-4 h-4" />
                                All items passed inspection
                              </div>
                            )}

                            {/* Notes */}
                            {inspection.notes && (
                              <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
                                <span className="font-semibold">Notes:</span> {inspection.notes}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Load more button */}
        {hasMore && !loading && (
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={handleLoadMore}
            >
              Load More
            </Button>
          </div>
        )}

        {loading && inspections.length > 0 && (
          <div className="p-2 text-center text-xs text-muted-foreground">
            Loading...
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default VehicleInspectionDropdown;
